import { useState, useEffect, useMemo, Fragment } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import OvertimeKpis from '../components/Overtime/OvertimeKpis';
import CostCenterChart from '../components/Overtime/CostCenterChart';
import CostCenterTrend from '../components/Overtime/CostCenterTrend';
import ByRolePanel from '../components/Overtime/ByRolePanel';
import IntensityPanel from '../components/Overtime/IntensityPanel';
import OvertimeTrend from '../components/Overtime/OvertimeTrend';
import CostShare from '../components/Overtime/CostShare';
import LensShareTrend from '../components/Overtime/LensShareTrend';
import BudgetVsActual from '../components/Overtime/BudgetVsActual';
import YoyPanel from '../components/Overtime/YoyPanel';
import EmployeeModal from '../components/Overtime/EmployeeModal';
import { fmtMoney, fmtHours, fmtNumber } from '../components/Overtime/format';
import { c, tbl } from '../theme';
import PageHeader from '../components/Shared/PageHeader';

// ── Period scope ────────────────────────────────────────────────────────────
// One menu holds every way of looking at the data: year to date, a single
// calendar month, or a single pay period. The unit underneath is always a pay
// period, not a date — a period counts toward the month and year it ENDS in
// (see the resolvePeriodScope note in routes/overtime.js). Payroll runs weekly,
// so a month is the four or five runs that ended in it, and the header states
// the exact span so a total is never mistaken for a calendar-month figure.
//
// Menu values are prefixed so one <select> can drive three different requests:
//   'ytd' | 'm:YYYY-MM' | 'p:<period uuid>'
const VIEW_YTD = 'ytd';

// ── Lens ────────────────────────────────────────────────────────────────────
// The enhancement brief asks for the indirect diagnostic and the fleet
// crossover as MODULES of this board rather than as their own dashboards. A
// lens is what that means in practice: the same page, the same panels, one
// filter on the job-profile mapping. Adding a dashboard per document would be
// the thing the brief explicitly says not to do.
//
// It lives in the URL so a lens is a link. "The indirect board" has to be
// something a person can send to someone else, or it cannot retire the file it
// is meant to replace.
// ── Levels ──────────────────────────────────────────────────────────────────
// The TPS framing the whole enhancement pack is organized around. A level is a
// DECISION, and the time grain follows from it:
//
//   L0  monthly   are we where we committed to be, and what changed?
//   L1  weekly    did we make the week, and which sites are off?
//   L2  daily     will we make the week? where do I intervene today?
//
// So the level drives the period menu rather than sitting beside it as a second
// unrelated control. Picking L0 and then reading a single pay period against a
// monthly budget is the mistake this arrangement removes.
//
// L2 is deliberately unbuilt rather than hidden. It needs a DAILY time extract
// from Workday -- hours, not payroll dollars, which are settled only after the
// week closes. Showing the level greyed with the reason keeps the gap in front
// of the people who can authorize the extract; dropping the tab would quietly
// make it somebody else's problem. What it must never be is last week's closed
// figures labelled "week to date".
// How the cost-center panels are grouped. "Group" is the level above the yard --
// the pack calls it a division, Athens calls it a group, and the label follows
// Athens. The URL parameter and the database column stay `division`, because
// renaming a column is a migration and a stored value is not what a reader sees.
//
// Type separates the yards from the corporate departments that share the same
// flat list. Both are drafted assignments (migration 038) awaiting Operations,
// so the page says so rather than presenting them settled.
// HR's four labor categories, verbatim from the Job Profile Classification
// Bible. Closed because the schema constrains it, so it is a constant rather
// than another request. The Lens keeps working alongside this: the Indirect
// lens is the two-way rollup, a category is the finer cut inside it.
const CATEGORIES = ['Direct Labor', 'Indirect Labor', 'G&A Labor', 'Sales Labor'];

const CC_GROUPS = [
  { key: 'cc',       label: 'Cost center', field: null },
  { key: 'division', label: 'Group',       field: 'division' },
  { key: 'type',     label: 'Type',        field: 'location_type' },
];

const TYPE_LABELS = {
  yard: 'Yards', mrf: 'MRFs', landfill: 'Landfills', transfer_station: 'Transfer stations',
  maintenance: 'Maintenance', office: 'Offices', department: 'Departments', overhead: 'Overhead',
};

const LEVELS = [
  { key: 'l0', label: 'L0 · Executive', grain: 'month',  hint: 'Monthly' },
  { key: 'l1', label: 'L1 · Site',      grain: 'period', hint: 'Weekly' },
  { key: 'l2', label: 'L2 · Supervisor', grain: 'day',   hint: 'Daily', disabled: true,
    why: 'Needs a daily time extract from Workday. Payroll only closes after the week ends, so pacing cannot be answered from it.' },
];

const LENSES = [
  { key: 'all',      label: 'All overtime' },
  { key: 'indirect', label: 'Indirect (OT2)' },
  { key: 'fleet',    label: 'Fleet maintenance (OT3)' },
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

// period_end_iso is a plain 'YYYY-MM-DD' string from the API; older cached
// responses may only carry period_end, whose JSON form starts with the same
// ten characters.
const isoOf = (p) => String(p.period_end_iso || p.period_end || '').slice(0, 10);

const fmtSpanDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function OvertimeDashboard() {
  const { user, isAdmin, isFinanceReviewer, costCenters: morCostCenters } = useAuth();

  // Admins and finance reviewers see the full all-CC dashboard.
  // Reviewers (and read_only) are scoped to their assigned overtime CCs.
  const isAdminView = isAdmin || isFinanceReviewer;
  const canViewPeople = user?.role !== 'read_only';

  const [sp, setSp] = useSearchParams();
  const lens = LENSES.some((l) => l.key === sp.get('lens')) ? sp.get('lens') : 'all';
  const level = LEVELS.some((l) => l.key === sp.get('level') && !l.disabled) ? sp.get('level') : 'l1';
  const division = sp.get('division') || '';
  const category = CATEGORIES.includes(sp.get('category')) ? sp.get('category') : '';
  const levelDef = LEVELS.find((l) => l.key === level);
  const setLens = (next) => {
    const nextSp = new URLSearchParams(sp);
    if (next === 'all') nextSp.delete('lens'); else nextSp.set('lens', next);
    setSp(nextSp, { replace: true });
  };
  const setCategory = (next) => {
    const nextSp = new URLSearchParams(sp);
    if (!next) nextSp.delete('category'); else nextSp.set('category', next);
    setSp(nextSp, { replace: true });
  };
  const setDivision = (next) => {
    const nextSp = new URLSearchParams(sp);
    if (!next) nextSp.delete('division'); else nextSp.set('division', next);
    setSp(nextSp, { replace: true });
  };
  const setLevel = (next) => {
    setResolvedFrom(null);
    const nextSp = new URLSearchParams(sp);
    if (next === 'l1') nextSp.delete('level'); else nextSp.set('level', next);
    setSp(nextSp, { replace: true });
  };

  const [view,        setView]        = useState('');    // 'ytd' | 'm:YYYY-MM' | 'p:<uuid>'
  // Set only when switching to L0 silently converted a pay period into its
  // month. The conversion is correct, but it changes every figure on the page
  // at the same moment the pay-period options grey out, and an unexplained
  // change of number reads as a bug even when it is the right number.
  const [resolvedFrom, setResolvedFrom] = useState(null);
  const [ccGroup,     setCcGroup]     = useState('cc');
  const [expanded,    setExpanded]    = useState(null);  // { cc_key, cc_id, code, name } | null
  const [empId,       setEmpId]       = useState(null);  // employee_number for L3 modal
  const [empPeriodId, setEmpPeriodId] = useState(null);  // period the employee was clicked from

  // ── Periods ────────────────────────────────────────────────
  const { data: periods = [], isLoading: periodsLoading } = useQuery({
    queryKey: ['ot-periods'],
    queryFn:  () => api.get('/overtime/periods'),
  });

  // Every calendar month of every year that has payroll, so a year reads
  // January through December rather than jumping over its gaps. Months with no
  // payroll behind them are kept in the list but marked and disabled: an
  // unexplained absence invites "there was no overtime that month", while a
  // selectable month totalling $0 says it outright and is worse. `periods`
  // arrives newest-first, so the years come out newest-first too.
  const months = useMemo(() => {
    const counts = new Map();
    for (const p of periods) {
      const ym = isoOf(p).slice(0, 7);
      if (ym) counts.set(ym, (counts.get(ym) || 0) + 1);
    }
    const years = [...new Set([...counts.keys()].map((ym) => ym.slice(0, 4)))].sort().reverse();
    return years.map((y) => ({
      year: y,
      months: MONTH_NAMES.map((name, i) => {
        const value = `${y}-${String(i + 1).padStart(2, '0')}`;
        return { value, label: `${name} ${y}`, count: counts.get(value) || 0 };
      }),
    }));
  }, [periods]);

  // Default to the most recent pay period, unchanged from before this menu
  // existed — the landing view stays what everyone is used to.
  useEffect(() => {
    if (!periods.length) return;
    const latestIso = isoOf(periods[0]);

    // L0 has no meaningful single-pay-period reading, so a period selection
    // carried in from L1 resolves to the month that period ENDED in -- the same
    // attribution resolvePeriodScope uses, so the two menus never disagree.
    if (level === 'l0') {
      if (!view || view.startsWith('p:')) {
        const from = view.startsWith('p:')
          ? (periods.find((p) => p.id === view.slice(2)) || periods[0])
          : null;
        const iso = from ? isoOf(from) : latestIso;
        setView(`m:${iso.slice(0, 7)}`);
        if (from) setResolvedFrom(from.label);
      }
      return;
    }
    // L1 opens on the most recent pay period, which is what everyone is used to.
    if (!view) setView(`p:${periods[0].id}`);
  }, [periods, view, level]);

  // The scope query string — one place, so the request and the reset-on-change
  // effect below can never drift apart.
  const scopeParams = useMemo(() => {
    if (!view) return null;
    if (view === VIEW_YTD)        return 'range=ytd';
    if (view.startsWith('m:'))    return `range=month&month=${view.slice(2)}`;
    if (view.startsWith('p:'))    return `period_id=${view.slice(2)}`;
    return null;
  }, [view]);

  // Which bar in the trend is the one the tiles are totalling. Without this the
  // chart and the KPIs read as two unrelated panels that happen to share a page.
  const activeBucket = useMemo(() => {
    if (level === 'l0') return view.startsWith('m:') ? view.slice(2) : null;
    if (!view.startsWith('p:')) return null;
    const sel = periods.find((p) => p.id === view.slice(2));
    return sel ? isoOf(sel) : null;
  }, [level, view, periods]);

  // Clicking a bar changes what the page is totalling, at the grain of the level.
  const selectBucket = (bucket) => {
    if (level === 'l0') { setView(`m:${bucket}`); return; }
    const hit = periods.find((p) => isoOf(p) === bucket);
    if (hit) setView(`p:${hit.id}`);
  };

  // There is deliberately no fallback period here any more. This used to read
  // "a month or YTD view has no one period to show line items for" and hand the
  // modal the LATEST period in the dataset — so a board on July opened a person
  // on whatever the newest pay run happened to be, and the same figures came up
  // under July and under year-to-date. The modal now takes the span (scopeParams)
  // whenever no bar has been clicked, and empPeriodId only when one has.

  // The year the budget panel reads. Taken from the month in view when there is
  // one, and otherwise from the newest pay period -- NOT from the wall clock. A
  // board opened in January against a year whose payroll has not started yet
  // would otherwise show an empty comparison and no reason for it.
  const budgetYear = view.startsWith('m:')
    ? Number(view.slice(2, 6))
    : (periods[0] ? Number(isoOf(periods[0]).slice(0, 4)) : new Date().getFullYear());

  // ── Summary (KPIs + by cost center) ────────────────────────
  // One request for every role. Scoping happens server-side against
  // user_overtime_cost_centers, so a reviewer gets their own cost centers here
  // without the page knowing anything about who they are.
  // The lens filters the KPIs and the cost-center ranking too, not just the role
  // table — a fleet lens showing company-wide tiles above a fleet table would be
  // read as the fleet's numbers.
  const lensQ = lens === 'all' ? '' : `&lens=${lens}`;
  const divQ  = division ? `&division=${encodeURIComponent(division)}` : '';
  const catQ  = category ? `&category=${encodeURIComponent(category)}` : '';
  const filterQ = `${lensQ}${divQ}${catQ}`;
  // The same scope with the ROLE dimension removed. A share needs a denominator,
  // and the denominator for "how much of overtime is fleet maintenance" is all
  // overtime in the same cost centers -- not all overtime in the company, which
  // would shrink every reading whenever a group was selected. division is a
  // cost-center filter and stays; lens and category are role filters and go.
  const baseQ = divQ;

  // Which grain the role panel opens on. The lens PINS a role column, so
  // defaulting to group under lens=fleet draws exactly one bar labelled
  // Maintenance -- a ranked chart of a single class, where length is the message
  // and there is nothing to compare it against. Fleet therefore opens a level
  // finer, at job family, which is where the maintenance cost actually splits
  // (Support Mechanic, Container Maintenance, MRF Maintenance, and the rest).
  const roleDefaultBy = lens === 'fleet' ? 'family' : (level === 'l0' ? 'family' : 'group');
  const lensLabel = LENSES.find((l) => l.key === lens)?.label || 'This lens';
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['ot-summary', scopeParams, filterQ],
    queryFn:  () => api.get(`/overtime/summary?${scopeParams}${filterQ}`),
    enabled:  !!scopeParams,
  });

  // Reset drill-downs whenever the scope changes — an expanded cost center or
  // open employee modal belongs to the span it was opened from.
  useEffect(() => { setExpanded(null); setEmpId(null); }, [scopeParams]);

  // Memoised because groupedCc depends on it: the `|| []` fallback is a fresh
  // array every render, which would re-aggregate on every keystroke elsewhere.
  const byCc      = useMemo(() => summary?.by_cost_center || [], [summary]);
  const scope     = summary?.scope || null;
  const priorSpan = summary?.prior_period?.span || null;
  const coverage  = summary?.coverage || null;
  const baseline  = summary?.baseline || null;
  const headcount = summary?.headcount || null;
  const runCount  = summary?.run_count || 0;
  const divisions = summary?.divisions || [];

  // Regrouping happens here rather than in a second request: these are the same
  // rows the cost-center table already has, so a division total cannot disagree
  // with the cost centers it is made of.
  const groupedCc = useMemo(() => {
    const field = CC_GROUPS.find((g) => g.key === ccGroup)?.field;
    if (!field) return byCc;

    const acc = new Map();
    for (const row of byCc) {
      // A cost center in payroll but absent from the reference table has no
      // division. It gets its own bucket instead of quietly joining a real one.
      const raw = row[field] || null;
      const key = raw || '(unassigned)';
      const cur = acc.get(key) || { cc_key: `g:${key}`, cc_id: null, code: null,
                                    name: field === 'location_type' ? (TYPE_LABELS[raw] || key) : key,
                                    ot_hours: 0, ot_amount: 0, employee_count: 0, members: 0,
                                    run_amount: 0, headcount: row.headcount == null ? null : 0,
                                    baseline_run_amount: row.baseline_run_amount == null ? null : 0,
                                    unassigned: !raw };
      cur.ot_hours      += Number(row.ot_hours || 0);
      cur.ot_amount     += Number(row.ot_amount || 0);
      cur.run_amount    += Number(row.run_amount || 0);
      if (cur.headcount != null) cur.headcount += Number(row.headcount || 0);
      if (cur.baseline_run_amount != null) cur.baseline_run_amount += Number(row.baseline_run_amount || 0);
      // Employees are counted distinctly per cost center upstream, so summing
      // double-counts anyone working across two. Reported as cost centers.
      cur.members       += 1;
      acc.set(key, cur);
    }
    // Per head is recomputed from the summed hours and heads, never averaged
    // across the member rows -- an average of ratios weights a ten-person office
    // the same as a four-hundred-person yard.
    for (const g of acc.values()) {
      g.ot_hours_per_head = g.headcount && runCount ? g.ot_hours / runCount / g.headcount : null;
    }
    return [...acc.values()].sort((a, b) => b.ot_amount - a.ot_amount);
  }, [byCc, ccGroup, runCount]);

  const grouping = ccGroup !== 'cc';

    // A viewer scoped to exactly one cost center gets that cost center's trend
  // directly. The ranked chart would be a single bar and the table a single row
  // that has to be expanded to reach the thing they came for — this is the same
  // CostCenterTrend the admin table opens, just without the two clicks.
  const singleCc = byCc.length === 1;

  const toggleCc = (row) => {
    setExpanded((cur) => (cur && cur.cc_key === row.cc_key ? null : row));
  };

  // A person opened from the intensity panel belongs to the whole span, not to
  // one bar, so the modal gets no period and falls back to scopeParams.
  const openPerson = (eId) => { setEmpId(eId); setEmpPeriodId(null); };

  // For drill-down we need a routable id: prefer the UUID, else the raw code.
  const drillId = (row) => row.cc_id || row.code;

  if (periodsLoading) return <LoadingSpinner />;

  // One dashboard for every role. There used to be a separate reviewer page
  // with no period control at all, which meant a reviewer could see their
  // history but could never ask "what did July look like". It rendered the same
  // CostCenterTrend this page opens on expand, so folding it in loses nothing.
  return (
    <div style={s.page}>
      <PageHeader
        title="Overtime"
        subtitle={<>Payroll overtime by cost center, type, and employee.{' '}
          <Link to="/overtime/definitions" style={s.defsLink}>What these numbers count</Link>.</>}
        alignActions="end"
        actions={
          <div style={s.scopeRow}>
            <div style={s.periodWrap}>
              <label style={s.periodLabel}>Level</label>
              <div style={s.levelSeg}>
                {LEVELS.map((l) => (
                  <button key={l.key} type="button" title={l.disabled ? l.why : l.hint}
                          disabled={l.disabled} onClick={() => !l.disabled && setLevel(l.key)}
                          style={{ ...s.levelBtn,
                                   ...(level === l.key ? s.levelOn : null),
                                   ...(l.disabled ? s.levelOff : null) }}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={s.periodWrap}>
              <label style={s.periodLabel}>View</label>
              <div style={s.selectWrap}>
              <select style={{ ...s.select, minWidth: '260px' }} value={view}
                      onChange={(e) => { setResolvedFrom(null); setView(e.target.value); }}>
                {!periods.length && <option value="">No periods available</option>}
                <option value={VIEW_YTD}>Year to Date</option>
                {months.map((y) => (
                  <optgroup key={y.year} label={`Months — ${y.year}`}>
                    {y.months.map((m) => (
                      <option key={m.value} value={`m:${m.value}`} disabled={!m.count}>
                        {m.label}{m.count ? '' : ' — not loaded'}
                      </option>
                    ))}
                  </optgroup>
                ))}
                {/* Pay periods are an L1 reading: at L0 the question is the
                    month, and offering both invites a single payroll run to be
                    compared against a monthly commitment.
                    They are DISABLED at L0, not removed. Removing them made the
                    menu silently shorter, and the first person to hit it read it
                    as the pay-period views having been deleted -- a control that
                    deletes another control's options with no visible reason is
                    indistinguishable from a regression. Greyed with the reason
                    in the group label says the same thing and can be acted on. */}
                {periods.length > 0 && (
                  <optgroup label={level === 'l0' ? 'Pay periods — switch to L1 · Site' : 'Pay periods'}>
                    {periods.map((p) => (
                      <option key={p.id} value={`p:${p.id}`} disabled={level === 'l0'}>{p.label}</option>
                    ))}
                  </optgroup>
                )}
              </select>
                <ChevronDown size={15} style={s.selectChev} />
              </div>
              {level === 'l0' && (
                <div style={s.viewHint}>
                  {resolvedFrom
                    ? `${resolvedFrom} became the month it ended in — L0 reads months.`
                    : 'L0 reads months. Pay periods are an L1 · Site view.'}
                </div>
              )}
            </div>

            <div style={s.periodWrap}>
              <label style={s.periodLabel}>Group</label>
              <div style={s.selectWrap}>
                <select style={{ ...s.select, minWidth: '180px' }} value={division}
                        onChange={(e) => setDivision(e.target.value)}>
                  <option value="">All groups</option>
                  {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <ChevronDown size={15} style={s.selectChev} />
              </div>
            </div>

            <div style={s.periodWrap}>
              <label style={s.periodLabel}>Category</label>
              <div style={s.selectWrap}>
                <select style={{ ...s.select, minWidth: '170px' }} value={category}
                        onChange={(e) => setCategory(e.target.value)}>
                  <option value="">All categories</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDown size={15} style={s.selectChev} />
              </div>
            </div>

            <div style={s.periodWrap}>
              <label style={s.periodLabel}>Lens</label>
              <div style={s.selectWrap}>
                <select style={{ ...s.select, minWidth: '200px' }} value={lens}
                        onChange={(e) => setLens(e.target.value)}>
                  {LENSES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                </select>
                <ChevronDown size={15} style={s.selectChev} />
              </div>
            </div>
          </div>
        }
      />

      {/* A filtered board must say so above the totals, not below them. */}
      {(lens !== 'all' || division || category) && (
        <div style={s.lensNote}>
          Showing {lens !== 'all' && <strong>{LENSES.find((l) => l.key === lens)?.label}</strong>}
          {lens !== 'all' && division && ' in '}
          {division && <strong>{division}</strong>}
          {category && <> {(lens !== 'all' || division) ? '· ' : ''}<strong>{category}</strong></>} only.
          Every figure on this page is filtered to that cut
          {lens !== 'all' && ', and excludes any job profile the mapping does not carry'}.
          {division && ' Group assignments are drafted and awaiting review by Operations.'}
        </div>
      )}

      {/* What the totals below actually cover. Spelled out because a month is a
          whole number of pay periods, not a calendar month. */}
      {scope && scope.mode !== 'period' && (
        <div style={s.scopeNote}>
          {scope.period_count === 0
            ? <>No pay periods have been loaded for <strong>{scope.label}</strong> yet.</>
            : <>
                <strong>{scope.label}</strong> · {scope.period_count} pay period{scope.period_count === 1 ? '' : 's'}
                {scope.start && scope.end && <> ending {fmtSpanDate(scope.start)} – {fmtSpanDate(scope.end)}</>}
                {scope.anchor && <> · counted back from the latest payroll loaded ({fmtSpanDate(scope.anchor)})</>}
                {priorSpan && (
                  <span style={s.scopeCompare}>
                    Change vs the previous {priorSpan.count} pay period{priorSpan.count === 1 ? '' : 's'}
                    {' '}({fmtSpanDate(priorSpan.start)} – {fmtSpanDate(priorSpan.end)})
                  </span>
                )}
              </>}
        </div>
      )}

      {/* A reviewer is not short of data because nothing was uploaded — they are
          short of it because their cost centers have none. Saying "ask a
          super-admin to import payroll" to someone who cannot import anything
          sends them down the wrong path. */}
      {/* States the scope plainly rather than warning about it. Nothing here
          records whether a viewer is MEANT to have every cost center, so a
          shortfall cannot be told apart from an intentionally narrow
          assignment — flagging 1-of-63 as a problem would cry wolf at the ten
          reviewers who correctly have exactly one. Stated as fact it still
          gives a reader the number to notice, and an assignment nobody
          intended shows up as an odd ratio. Catching drift for someone who can
          actually fix it belongs in User Management, not here: a reviewer
          cannot change their own assignments. */}
      {coverage && (
        <div style={s.coverageNote}>
          Showing <strong>{coverage.assigned} of {coverage.active}</strong> cost centers — the ones you are assigned to.
        </div>
      )}

      {!periods.length ? (
        <div style={s.emptyState}>
          {isAdminView ? (
            <>No overtime data has been uploaded yet. A super-admin can import a payroll
               file from the <strong>Overtime Upload</strong> screen.</>
          ) : (
            <>No overtime data is available for your assigned cost centers.</>
          )}
        </div>
      ) : summaryLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Level 1 — KPI cards */}
          <OvertimeKpis kpis={summary?.kpis} priorKpis={summary?.prior_kpis} />

          {/* L0 only. The budget is a monthly commitment, so reading it against
              a single pay period is the mistake the level split exists to
              prevent -- and L1's own question is "did we make the week", which
              a monthly target cannot answer. */}
          {level === 'l0' && <BudgetVsActual year={budgetYear} />}

          {/* Year over year sits directly under the budget and above the yard
              detail: "are we worse than last year" is asked before "which site",
              and both this and the budget answer "against what" rather than
              "how much". It is deliberately NOT gated to L0 -- the board opens
              on L1, and a panel only visible after switching level is the link
              this replaced. It states its own monthly grain instead. */}
          <YoyPanel filterQ={filterQ} canViewPeople={canViewPeople} />

          {singleCc ? (
            /* Scoped to one cost center — go straight to its trend. */
            <>
              <div style={s.sectionTitle}>{byCc[0].code} · {byCc[0].name}</div>
              <div style={s.reviewerCard}>
                <CostCenterTrend
                  filterQ={filterQ}
                  scopeParams={scopeParams}
                  ccId={drillId(byCc[0])}
                  ccName={`${byCc[0].code} · ${byCc[0].name}`}
                  canViewPeople={canViewPeople}
                  onSelectEmployee={canViewPeople ? (eId, pid) => { setEmpId(eId); setEmpPeriodId(pid); } : undefined}
                />
              </div>

              {/* The role cut survives the drill into one cost center. Scoping to
                  a single yard used to drop it entirely, which is the point at
                  which "which role is this overtime in" stops being answerable
                  and starts being the question the reader actually has. */}
              <ByRolePanel scopeParams={scopeParams} filterQ={filterQ} defaultBy={roleDefaultBy} />
              <IntensityPanel scopeParams={scopeParams} filterQ={filterQ}
                              onSelectEmployee={canViewPeople ? openPerson : undefined} />
            </>
          ) : (
          <>
          {/* The trend says whether overtime moved; the panel beside it says
              where that sits. Deliberately a different cut from the table at the
              bottom of the page, which is what makes it a second answer.
              auto-fit drops them to one column when the window cannot
              hold both, the same way ExpenseAnalysis lays its panels out. */}
          <div style={s.panelRow}>
            <div style={s.panelWide}>
              <OvertimeTrend
                grain={levelDef.grain}
                filterQ={filterQ}
                activeBucket={activeBucket}
                onSelectBucket={selectBucket}
              />
            </div>
            <div style={s.panelSide}>
              <CostShare scopeParams={scopeParams} filterQ={filterQ} />
            </div>
          </div>

          {/* A lens is filter state, so the trend above is ALREADY the lens's
              trend -- what a filtered chart cannot show is the denominator. This
              is the reading the lens takes away and does not give back. */}
          {lens !== 'all' && (
            <LensShareTrend grain={levelDef.grain} filterQ={filterQ} baseQ={baseQ}
                            label={lensLabel} />
          )}

          {/* Role sits above the cost-center block, not below it: which ROLE the
              overtime is in is the question the board is asked first, and the
              chart and table underneath are the same cut at two resolutions, so
              nothing may be wedged between them. It opens on a different cut
              from the composition panel above -- at L0 which job families carry
              the cost, at L1 which groups are running the week. */}
          <ByRolePanel scopeParams={scopeParams} filterQ={filterQ} defaultBy={roleDefaultBy} />

          {/* Who, after which role: the same span and filters, cut by person
              and by the manager they report to. */}
          <IntensityPanel scopeParams={scopeParams} filterQ={filterQ}
                          onSelectEmployee={canViewPeople ? openPerson : undefined} />

          <div style={s.groupHead}>
            <div style={s.sectionTitleFlush}>
              Overtime cost by {CC_GROUPS.find((g) => g.key === ccGroup)?.label.toLowerCase()}
            </div>
            <div style={s.levelSeg}>
              {CC_GROUPS.map((g) => (
                <button key={g.key} type="button" onClick={() => { setCcGroup(g.key); setExpanded(null); }}
                        style={{ ...s.groupBtn, ...(ccGroup === g.key ? s.levelOn : null) }}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {/* The two right-hand columns need a sentence, because "vs baseline"
              means nothing until the reader knows which runs it is. */}
          {(baseline || headcount) && (
            <div style={s.baselineNote}>
              <strong>Per run</strong> is the cost divided by the {runCount} pay run{runCount === 1 ? '' : 's'} in view.
              {baseline && <>
                {' '}<strong>vs baseline</strong> compares it with the same cost center's average over the
                {' '}{baseline.count} pay run{baseline.count === 1 ? '' : 's'} before ({fmtSpanDate(baseline.start)} – {fmtSpanDate(baseline.end)}).
                Holiday weeks pull a single run down, so read a one-week swing with that in mind.
              </>}
              {headcount?.available && <>
                {' '}<strong>OT hrs / head</strong> is weekly overtime hours divided by everyone on the
                {' '}cost center's HR roster as of {fmtSpanDate(headcount.snapshot_date)}, not only the people who had overtime.
              </>}
              {headcount?.reason === 'role_filter' &&
                ' OT hrs / head is left blank under a lens or category: the roster counts every head in the yard, so a filtered numerator would understate it.'}
              {headcount?.reason === 'no_roster_before_span' &&
                ' OT hrs / head is left blank: no HR roster was loaded on or before the end of this span.'}
            </div>
          )}

          {grouping && (
            <div style={s.draftNote}>
              Group and type are drafted assignments awaiting review by Operations.
              Twelve of the 63 cost centers carry a note questioning their assignment —
              treat these groupings as provisional.
            </div>
          )}

          <CostCenterChart
            data={grouping ? groupedCc : byCc}
            selectedKey={expanded?.cc_key}
            onSelect={grouping ? undefined : toggleCc}
          />

          {/* Level 1 table → Level 2 expand (trend + detail) */}
          <div style={s.sectionTitle}>{grouping ? 'Totals' : 'All Cost Centers'}</div>
          <div style={s.tableCard}>
            <div style={{ overflowX: 'auto' }}>
              {/* Wide table: it scrolls inside this, so the page does not. */}
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}></th>
                  <th style={s.th}>Cost Center</th>
                  <th style={s.thR}>Employees</th>
                  <th style={s.thR}>OT Hours</th>
                  <th style={s.thR}>OT Cost</th>
                  <th style={s.thR}>Per run</th>
                  <th style={s.thR}>vs baseline</th>
                  <th style={s.thR}>OT hrs / head</th>
                </tr>
              </thead>
              <tbody>
                {grouping && groupedCc.map((row) => (
                  <tr key={row.cc_key} style={s.tr}>
                    <td style={s.tdChev}></td>
                    <td style={s.tdName}>
                      {row.name}
                      <span style={s.memberCount}>
                        {row.members} cost center{row.members === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td style={s.tdR}>—</td>
                    <td style={s.tdR}>{fmtHours(row.ot_hours)}</td>
                    <td style={s.tdRb}>{fmtMoney(row.ot_amount)}</td>
                    <td style={s.tdR}>{fmtMoney(row.run_amount)}</td>
                    <td style={s.tdR}><VsBaseline run={row.run_amount} base={row.baseline_run_amount} /></td>
                    <td style={s.tdR}><PerHead row={row} /></td>
                  </tr>
                ))}
                {!grouping && byCc.map((row) => {
                  const isOpen = expanded?.cc_key === row.cc_key;
                  return (
                    <Fragment key={row.cc_key}>
                      <tr style={{ ...s.tr, ...(isOpen ? s.trOpen : {}) }}
                          onClick={() => toggleCc(row)}>
                        <td style={s.tdChev}>
                          {isOpen ? <ChevronDown size={15} color="#1B2A4A" /> : <ChevronRight size={15} color="#9CA3AF" />}
                        </td>
                        <td style={s.tdName}>
                          <span style={s.ccCode}>{row.code}</span> {row.name}
                        </td>
                        <td style={s.tdR}>{fmtNumber(row.employee_count)}</td>
                        <td style={s.tdR}>{fmtHours(row.ot_hours)}</td>
                        <td style={s.tdRb}>{fmtMoney(row.ot_amount)}</td>
                        <td style={s.tdR}>{fmtMoney(row.run_amount)}</td>
                        <td style={s.tdR}><VsBaseline run={row.run_amount} base={row.baseline_run_amount} /></td>
                        <td style={s.tdR}><PerHead row={row} /></td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={8} style={s.detailCell}>
                            <CostCenterTrend
                              ccId={drillId(row)}
                              filterQ={filterQ}
                              scopeParams={scopeParams}
                              ccName={`${row.code} · ${row.name}`}
                              canViewPeople={canViewPeople}
                              onSelectEmployee={canViewPeople ? (eId, pid) => { setEmpId(eId); setEmpPeriodId(pid); } : undefined}
                            />

                            {/* The same role cut, scoped to this cost center. A
                                yard's overtime being up is where the question
                                starts; which family it is in is the next thing
                                asked, and it used to require leaving the row. */}
                            <ByRolePanel scopeParams={scopeParams} filterQ={filterQ}
                                         ccId={drillId(row)} defaultBy="family" />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {!byCc.length && <tr><td colSpan={8} style={s.empty}>No overtime recorded for this selection.</td></tr>}
              </tbody>
            </table>
            </div>
          </div>

          </>
          )}
        </>
      )}

      {/* Level 3 — employee modal (use the period that was active in the trend chart) */}
      {empId && (
        <EmployeeModal empId={empId} periodId={empPeriodId} scopeParams={scopeParams}
                       filterQ={filterQ} onClose={() => { setEmpId(null); setEmpPeriodId(null); }} />
      )}
    </div>
  );
}

// A cost center's run rate against its own recent normal. Over is red and
// under is green, the board's convention -- less overtime is the goal. Growth
// from no baseline has no percentage, so it says "new" and the per-run column
// carries the size. No baseline at all (the first runs ever loaded) is a dash.
function VsBaseline({ run, base }) {
  if (base == null) return <span style={s.faint}>—</span>;
  if (base < 1)     return <span style={s.faint}>new</span>;
  const pct = ((run - base) / base) * 100;
  if (Math.abs(pct) < 0.5) return <span style={s.faint}>0%</span>;
  return (
    <span style={{ color: pct > 0 ? c.dangerText : c.successText, fontWeight: 600 }}>
      {pct > 0 ? '+' : ''}{pct.toFixed(0)}%
    </span>
  );
}

// Zero heads on the roster with overtime in payroll is a mapping gap between
// the two feeds, not infinite overtime per person, so it is named as such.
function PerHead({ row }) {
  if (row.headcount == null) return <span style={s.faint}>—</span>;
  if (!row.headcount) return <span style={s.faint} title="Overtime in payroll, but nobody on the HR roster for this cost center">no roster</span>;
  return <span title={`${fmtNumber(row.headcount)} on the roster`}>{fmtHours(row.ot_hours_per_head)}</span>;
}

const s = {
  page:        { maxWidth: '1100px', margin: '0 auto' },
  // Top-aligned, not bottom-aligned. Every column here is a label above a
  // control, so aligning the tops aligns the labels AND the controls -- while
  // aligning the bottoms only did so for as long as every column was exactly
  // two elements tall. The View column carries an explanatory line at L0, and
  // under flex-end that line took the place of the control on the shared
  // baseline and pushed its own select and label up out of the row.
  scopeRow:    { display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' },
  groupHead:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', margin: '26px 0 10px', flexWrap: 'wrap' },
  viewHint:    { fontSize: '11px', color: '#6B7280', marginTop: '5px', maxWidth: '260px', lineHeight: 1.35 },
  sectionTitleFlush:{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280' },
  groupBtn:         { appearance: 'none', border: 'none', background: 'transparent', cursor: 'pointer', padding: '6px 12px', fontSize: '12px', fontFamily: 'inherit', color: '#374151', borderRight: '1px solid #E5E7EB' },
  memberCount:      { marginLeft: '8px', fontSize: '11px', color: '#9CA3AF' },
  baselineNote:     { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#4B5563', marginBottom: '10px' },
  faint:            { color: c.textFaint },
  draftNote:        { background: '#EFF6FF', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#374151', marginBottom: '10px' },
  panelRow:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '18px' },
  panelWide:   { minWidth: 0 },
  panelSide:   { minWidth: 0 },
  levelSeg:    { display: 'flex', border: '1px solid #D1D5DB', borderRadius: '8px', overflow: 'hidden', background: '#fff' },
  levelBtn:    { appearance: 'none', border: 'none', background: 'transparent', cursor: 'pointer', padding: '8px 12px', fontSize: '12px', fontFamily: 'inherit', color: '#374151', borderRight: '1px solid #E5E7EB', whiteSpace: 'nowrap' },
  levelOn:     { background: '#1B2A4A', color: '#E8EEF7', fontWeight: 600 },
  levelOff:    { color: '#9CA3AF', cursor: 'not-allowed', background: '#F9FAFB' },
  defsLink:    { color: '#1D4ED8', textDecoration: 'none' },
  lensNote:    { background: '#EFF6FF', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#374151', marginBottom: '18px' },
  scopeNote:   { background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#4B5563', marginBottom: '18px' },
  scopeCompare:{ display: 'block', marginTop: '3px', fontSize: '11px', color: '#6B7280' },
  coverageNote:{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#4B5563', marginBottom: '18px' },
  periodWrap:  { display: 'flex', flexDirection: 'column', gap: '5px' },
  periodLabel: { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280' },
  selectWrap:  { position: 'relative' },
  select:      { appearance: 'none', padding: '8px 32px 8px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', color: '#374151', background: '#fff', fontFamily: 'inherit', minWidth: '240px', cursor: 'pointer' },
  selectChev:  { position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none' },
  sectionTitle:{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6B7280', margin: '26px 0 10px' },
  tableCard:   { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:          { ...tbl.th, ...tbl.th },
  thR:         { ...tbl.thR, ...tbl.thR },
  tr:          { ...tbl.tr, ...tbl.trHover },
  trOpen:      { background: '#F9FAFB' },
  tdChev:      { padding: '10px 8px 10px 14px', width: '30px' },
  tdName:      { padding: '10px 14px', color: '#111827' },
  ccCode:      { display: 'inline-block', fontSize: '11px', fontWeight: 700, color: '#4A6080', background: '#EEF2F7', padding: '1px 6px', borderRadius: '4px', marginRight: '6px' },
  tdR:         { ...tbl.tdR, ...tbl.tdR },
  tdRb:        { padding: '10px 14px', color: '#111827', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  detailCell:  { padding: 0, background: '#F9FAFB' },
  empty:       { textAlign: 'center', padding: '28px', color: '#9CA3AF' },
  emptyState:  { background: '#fff', border: '1px dashed #D1D5DB', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#6B7280', fontSize: '13px', lineHeight: 1.6 },
  reviewerCard:{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden', marginBottom: '20px' },
};
